import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Book, HelpCircle, FileText, ChevronRight, Search, Loader2 } from 'lucide-react';
import { knowledgeService } from '@/services/knowledge.service';
import { CategoryCard } from '@/components/CategoryCard';
import { ArticleCard } from '@/components/ArticleCard';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalConfigStore } from '@/stores/portalConfigStore';

export function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { config } = usePortalConfigStore();

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => knowledgeService.getCategories(),
  });

  const { data: featuredArticles, isLoading: articlesLoading } = useQuery({
    queryKey: ['featured-articles'],
    queryFn: () => knowledgeService.getFeaturedArticles(6),
  });

  const { data: popularSearches } = useQuery({
    queryKey: ['popular-searches'],
    queryFn: () => knowledgeService.getPopularSearches(),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section
        className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-16 md:py-24"
        style={config.primaryColor ? {
          background: `linear-gradient(to bottom right, ${config.primaryColor}, ${config.primaryColor}dd)`,
        } : undefined}
      >
        <div className="container text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            {config.welcomeMessage || 'How can we help you?'}
          </h1>
          <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Search our knowledge base or browse categories to find the answers you need.
          </p>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search for articles, guides, and more..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg text-gray-900 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-300 shadow-lg dark:text-gray-100"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Search
              </button>
            </div>
          </form>

          {/* Popular Searches */}
          {popularSearches?.queries && popularSearches.queries.length > 0 && (
            <div className="mt-6">
              <p className="text-sm text-primary-200 mb-2">Popular searches:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {popularSearches.queries.slice(0, 5).map((query) => (
                  <Link
                    key={query}
                    to={`/search?q=${encodeURIComponent(query)}`}
                    className="px-3 py-1 bg-white/10 rounded-full text-sm hover:bg-white/20 transition-colors dark:bg-gray-800"
                  >
                    {query}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Browse by Category</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Find help organized by topic</p>
            </div>
          </div>

          {categoriesLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : Array.isArray(categories) && categories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Book className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
              <p>No categories available yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Featured Articles Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-950">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Featured Articles</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Most popular help articles</p>
            </div>
            <Link
              to="/search"
              className="flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium"
            >
              View all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {articlesLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : Array.isArray(featuredArticles) && featuredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
              <p>No featured articles yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container">
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 md:p-12 text-center">
            <HelpCircle className="h-12 w-12 mx-auto mb-4 text-primary-600 dark:text-primary-400" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Can't find what you're looking for?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-xl mx-auto">
              Our support team is here to help. Submit a request and we'll get back to you as
              soon as possible.
            </p>
            <Link
              to="/submit-ticket"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Submit a Request
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
